import React, { memo } from 'react';
import { Grid, Typography } from '@material-ui/core';
import Img from 'react-cool-img';
import { IoMdHeartDislike } from 'react-icons/io';
import { WarningIcon } from '../styles';
import {
  PLACEHOLDER_IMAGE,
  getCurrencySymbol,
  msrpOf,
  wholesaleOf,
  productQuantity,
  productTotal,
  sortSizes,
} from '../utils';

const quantityInputStyle = {
  border: '1px solid rgba(0, 0, 0, 0.5)',
  width: 46,
  height: 22,
  fontSize: 12,
  textAlign: 'right',
  paddingRight: 4,
};

const SizeColumn = ({ size, editable, quantity, onChange }) => (
  <Grid style={{ marginRight: 8 }}>
    <Typography align="right" style={{ marginBottom: 8, fontSize: 12 }}>
      {size}
    </Typography>
    {editable ? (
      <input
        style={quantityInputStyle}
        value={quantity == null ? '' : quantity}
        onChange={event => onChange(size, event.target.value)}
      />
    ) : (
      <Typography align="right" style={{ fontSize: 12, width: 46, height: 22 }}>
        {quantity || 0}
      </Typography>
    )}
  </Grid>
);

const RemoveFromCollection = ({ onClick, style }) => (
  <Grid
    container
    alignItems="center"
    onClick={onClick}
    style={{ cursor: 'pointer', ...style }}
  >
    <Typography style={{ fontSize: 12, marginRight: 10 }}>
      Remove from collection
    </Typography>
    <IoMdHeartDislike style={{ position: 'relative', top: 1.5 }} size={12} />
  </Grid>
);

function CartItem({
  product,
  currency,
  isMobile,
  nav,
  isEmpty,
  onChangeQuantity,
  onRemove,
}) {
  const symbol = getCurrencySymbol(currency);
  const quantity = productQuantity(product);
  const total = productTotal(product, currency);
  const sizes = product.sizes ? sortSizes(Object.keys(product.sizes)) : [];
  const editable = nav === 'place';
  const warningColor = isEmpty ? '#E01919' : undefined;

  const quantityOf = size => {
    const info = (product.cartInfo || []).find(item => item.size === size);
    return info ? info.quantity : undefined;
  };

  const handleChange = (size, value) =>
    onChangeQuantity(product.style_code, size, value);

  const sizeColumns = sizes.map(size => (
    <SizeColumn
      key={size}
      size={size}
      editable={editable}
      quantity={quantityOf(size)}
      onChange={handleChange}
    />
  ));

  return (
    <>
      <Grid
        container
        justifyContent="space-between"
        style={{
          paddingLeft: 11,
          paddingRight: 17,
          width: isMobile ? '95%' : '75%',
          margin: isMobile ? '20px 0px 10px' : '40px 0px',
          flexWrap: 'nowrap',
        }}
      >
        <Img
          onClick={() => onRemove(product)}
          src={product.mainly_image || PLACEHOLDER_IMAGE}
          alt="farm"
          style={{
            width: isMobile ? 87 : 100,
            height: isMobile ? 120 : 'auto',
            marginRight: isMobile ? 10 : 22,
          }}
        />
        <Grid container>
          <Grid
            container
            alignItems="space-between"
            justifyContent="space-between"
            style={{
              paddingBottom: 5,
              borderBottom: '1px solid rgba(0, 0, 0, 0.5)',
            }}
          >
            <Grid style={{ display: isMobile && 'flex' }}>
              <Grid>
                <Typography
                  style={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    lineHeight: '21px',
                    color: warningColor,
                  }}
                >
                  {product.description}{' '}
                  {isEmpty && (
                    <span style={{ marginLeft: 8 }}>
                      <WarningIcon />
                      Please review this item
                    </span>
                  )}
                </Typography>
                <Typography style={{ fontSize: 12, color: warningColor }}>
                  {product.style_code}
                </Typography>
              </Grid>
            </Grid>
            <Grid style={{ marginTop: product.description.length < 28 && 10 }}>
              <Grid>
                <Typography
                  display="inline"
                  align="right"
                  style={{ fontWeight: 'bold', fontSize: 12, marginRight: 8 }}
                >
                  Suggested Retail{' '}
                </Typography>
                <Typography
                  display="inline"
                  style={{ fontWeight: 'normal', fontSize: 12 }}
                >
                  <span>
                    {symbol} {Number(msrpOf(product, currency)).toFixed(2)}
                  </span>
                </Typography>
              </Grid>
              <Grid
                container
                justifyContent={!isMobile && 'flex-end'}
                style={{ marginTop: 3 }}
              >
                <Typography
                  display="inline"
                  align="right"
                  style={{ fontWeight: 'bold', fontSize: 12, marginRight: 9 }}
                >
                  Wholesale{' '}
                </Typography>
                <Typography
                  display="inline"
                  style={{ fontWeight: 'normal', fontSize: 12 }}
                >
                  <span>{symbol}</span>{' '}
                  {(
                    Math.round(wholesaleOf(product, currency) * 100) / 100
                  ).toFixed(2)}
                </Typography>
              </Grid>
              {isMobile && editable && (
                <RemoveFromCollection onClick={() => onRemove(product)} />
              )}
            </Grid>
          </Grid>
          {!isMobile && (
            <Grid
              container
              justifyContent="space-between"
              style={{
                paddingBottom: 14,
                marginTop: 8,
                borderBottom: editable && '1px solid rgba(0, 0, 0, 0.5)',
              }}
            >
              <Grid>
                <Grid container>
                  <Typography
                    display="inline"
                    style={{ fontWeight: 'bold', marginRight: 19 }}
                  >
                    Sizes
                  </Typography>
                  {sizeColumns}
                </Grid>
              </Grid>
              <Grid>
                <Grid container>
                  <Grid style={{ marginRight: 21 }}>
                    <Typography
                      style={{
                        fontWeight: 'bold',
                        fontSize: 12,
                        marginBottom: 11,
                        color: warningColor,
                      }}
                    >
                      Quantity
                    </Typography>
                    <Typography
                      align="right"
                      style={{ fontSize: 12, color: warningColor }}
                    >
                      {quantity}
                    </Typography>
                  </Grid>
                  <Grid>
                    <Typography
                      align="right"
                      style={{
                        fontWeight: 'bold',
                        fontSize: 12,
                        marginBottom: 11,
                        color: warningColor,
                      }}
                    >
                      Total
                    </Typography>
                    <Typography
                      style={{
                        fontWeight: 'bold',
                        fontSize: 12,
                        color: warningColor,
                      }}
                    >
                      {total ? <span>{symbol}</span> : null} {total}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          {!isMobile && editable && (
            <RemoveFromCollection
              onClick={() => onRemove(product)}
              style={{ margin: '6px 0px' }}
            />
          )}
        </Grid>
      </Grid>
      {isMobile && (
        <Grid
          container
          justifyContent="space-between"
          style={{
            paddingBottom: 14,
            paddingLeft: 11,
            paddingRight: 17,
            borderBottom: '1px solid rgba(0, 0, 0, 0.5)',
          }}
        >
          <Grid>
            <Grid container>
              <Grid>
                <Typography
                  style={{ fontWeight: 'bold', marginRight: 19, fontSize: 12 }}
                >
                  Sizes
                </Typography>
              </Grid>
              <Grid>
                <Grid container>{sizeColumns}</Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent="space-between"
            style={{ marginTop: 5 }}
          >
            <Grid style={{ marginRight: 21 }}>
              <Typography
                display="inline"
                style={{
                  fontWeight: 'bold',
                  fontSize: 12,
                  marginRight: 10,
                  marginBottom: 11,
                }}
              >
                Quantity
              </Typography>
              <Typography
                display="inline"
                align="right"
                style={{ fontSize: 12 }}
              >
                {quantity}
              </Typography>
            </Grid>
            <Grid>
              <Typography
                display="inline"
                align="right"
                style={{
                  fontWeight: 'bold',
                  fontSize: 12,
                  marginBottom: 11,
                  marginRight: 10,
                }}
              >
                Total
              </Typography>
              <Typography
                display="inline"
                style={{ fontWeight: 'bold', fontSize: 12 }}
              >
                {total ? <span>{symbol}</span> : null} {total}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
}

export default memo(CartItem);
